<div class="flex w-full flex-col gap-8">
  <!-- Patient Reference Section -->
  <div class="py-8">
    <div class="flex justify-between">
      <h2 class="!m-0 !font-medium">Patient Reference</h2>
      <button mat-icon-button (click)="editPatientReference()">
        <mat-icon>edit</mat-icon>
      </button>
    </div>

    @if (patientReferenceForm(); as patientReference) {
      <div class="flex flex-col">
        <div
          class="grid grid-cols-1 gap-4 rounded-lg bg-blue-50 p-4 md:grid-cols-2"
        >
          <pr-acc-control-summary
            label="Claim Number"
            [control]="patientReference.controls.claimNumber"
          />
          <pr-acc-control-summary
            label="Provider"
            [control]="patientReference.controls.provider"
            [formatFn]="providerDisplayFn"
          />
        </div>
      </div>
    }
  </div>
  <!-- Patient Details Section -->
  <div class="py-8">
    <div class="flex justify-between">
      <h2 class="!m-0 !font-medium">Patient Details</h2>
      <button mat-icon-button (click)="editPatientDetails()">
        <mat-icon>edit</mat-icon>
      </button>
    </div>

    @if (patientForm(); as patient) {
      <div class="flex flex-col">
        <!-- Patient Details -->
        <div
          class="grid grid-cols-1 gap-4 rounded-lg bg-blue-50 p-4 md:grid-cols-2"
        >
          <pr-acc-control-summary
            label="First Name"
            [control]="patient.details.controls.firstName"
          />
          <pr-acc-control-summary
            label="Middle Name(s)"
            [control]="patient.details.controls.middleName"
          />
        </div>

        <div class="grid grid-cols-1 gap-4 p-4 md:grid-cols-2">
          <pr-acc-control-summary
            label="Surname"
            [control]="patient.details.controls.surname"
          />
          <pr-acc-control-summary
            label="NHI number"
            [control]="patient.details.controls.nhi"
          />
        </div>

        <div
          class="grid grid-cols-1 gap-4 rounded-lg bg-blue-50 p-4 md:grid-cols-2"
        >
          <pr-acc-control-summary
            label="Date of Birth"
            [control]="patient.details.controls.dateOfBirth"
            [formatFn]="dateFormatFn"
          />
          <pr-acc-control-summary
            label="Gender"
            [control]="patient.details.controls.gender"
            [lookUp]="accGender"
            [reverseLookUp]="true"
          />
        </div>

        <div class="grid grid-cols-1 gap-4 p-4 md:grid-cols-2">
          <pr-acc-control-summary
            label="Ethnicity"
            [control]="patient.details.controls.ethnicityCode"
            [lookUp]="accEthnicityMap"
          />
          <pr-acc-control-summary
            label="Other Ethnicity Details"
            [control]="patient.details.controls.otherEthnicity"
          />
        </div>

        <div
          class="grid grid-cols-1 gap-4 rounded-lg bg-blue-50 p-4 md:grid-cols-2"
        >
          <!-- Patient Home Address -->
          <div class="flex flex-col">
            <div class="font-bold">Home Address</div>
            <pr-acc-control-summary
              label="Address Line 1"
              [showLabel]="false"
              [control]="patient.address.controls.line1"
            />
            <pr-acc-control-summary
              label="Address Line 2"
              [showLabel]="false"
              [control]="patient.address.controls.line2"
            />
            <pr-acc-control-summary
              label="Suburb"
              [showLabel]="false"
              [control]="patient.address.controls.suburb"
            />
            <pr-acc-control-summary
              label="City"
              [showLabel]="false"
              [control]="patient.address.controls.city"
            />
            <pr-acc-control-summary
              label="Postcode"
              [showLabel]="false"
              [control]="patient.address.controls.postCode"
            />
            <pr-acc-control-summary
              label="Country"
              [showLabel]="false"
              [control]="patient.address.controls.country"
            />
          </div>

          <!-- Patient Contact Details -->
          <div class="flex flex-col gap-4">
            <pr-acc-control-summary
              label="Mobile Phone"
              [control]="patient.contact.controls.mobilePhone"
            />
            <pr-acc-control-summary
              label="Home Phone"
              [control]="patient.contact.controls.homePhone"
            />
            <pr-acc-control-summary
              label="Work Phone"
              [control]="patient.contact.controls.workPhone"
            />
            <pr-acc-control-summary
              label="Email Address"
              [control]="patient.contact.controls.emailAddress"
            />
          </div>
        </div>
      </div>
    }
  </div>
  <!-- Accident Details Section -->
  <div class="py-8">
    <div class="flex justify-between">
      <h2 class="!m-0 !font-medium">Accident Details</h2>
      <button mat-icon-button (click)="editAccidentDetails()">
        <mat-icon>edit</mat-icon>
      </button>
    </div>

    @if (injuryForm(); as injury) {
      <div class="flex flex-col">
        <div
          class="grid grid-cols-1 gap-4 rounded-lg bg-blue-50 p-4 md:grid-cols-2"
        >
          <pr-acc-control-summary
            label="Date of Injury"
            [control]="injury.controls.accidentDate"
            [formatFn]="dateFormatFn"
          />
          <pr-acc-control-summary
            label=" Did the accident involve a moving motor vehicle on a public road?"
            [control]="injury.controls.involvesVehicle"
            [lookUp]="accBooleanDisplayMap"
            [compactWarning]="true"
          />
        </div>

        <div class="grid grid-cols-1 gap-4 p-4 md:grid-cols-2">
          <pr-acc-control-summary
            label="Is this a claim for treatment injury?"
            [control]="injury.controls.medicalTreatmentInjury"
            [lookUp]="accBooleanDisplayMap"
            [compactWarning]="true"
          />
          <pr-acc-control-summary
            label="Accident Scene"
            [control]="injury.controls.accidentSceneCode"
            [lookUp]="accSceneMap"
          />
        </div>

        <div
          class="grid grid-cols-1 gap-4 rounded-lg bg-blue-50 p-4 md:grid-cols-2"
        >
          <pr-acc-control-summary
            label="Accident Location"
            [control]="injury.controls.accidentLocationCode"
            [lookUp]="accGeolocationsMap"
          />
          <pr-acc-control-summary
            label="Describe the events or circumstances which led to the injury"
            [control]="injury.controls.causeOfAccident"
            [compactWarning]="true"
          />
        </div>

        <div class="grid grid-cols-1 gap-4 p-4 md:grid-cols-2">
          <pr-acc-control-summary
            label=" Did the accident happen during sports or recreation?"
            [control]="injury.controls.sportingInjury"
            [lookUp]="accBooleanDisplayMap"
            [compactWarning]="true"
          />
          @if (injury.displaySportNameCode) {
            <pr-acc-control-summary
              label="Sport/Recreation Activity"
              [control]="injury.controls.sportNameCode"
              [lookUp]="accSportsMap"
            />
          }
        </div>

        <div
          class="grid grid-cols-1 gap-4 rounded-lg bg-blue-50 p-4 md:grid-cols-2"
        >
          <pr-acc-control-summary
            label="Additional Comments About the Injury"
            [control]="injury.controls.injuryComments"
            [compactWarning]="true"
          />
        </div>
      </div>
    }
  </div>

  <!-- Employment Details Section -->
  <div class="py-8">
    <div class="flex justify-between">
      <h2 class="!m-0 !font-medium">Employment Details</h2>
      <button mat-icon-button (click)="editEmploymentDetails()">
        <mat-icon>edit</mat-icon>
      </button>
    </div>

    @if (employmentForm(); as employment) {
      <div class="flex flex-col">
        <div
          class="grid grid-cols-1 gap-4 rounded-lg bg-blue-50 p-4 md:grid-cols-2"
        >
          <pr-acc-control-summary
            label="Patient occupation"
            [control]="employment.controls.occupationCode"
            [formatFn]="optionDisplayFn"
          />
          <pr-acc-control-summary
            label="Is the patient in paid employment?"
            [control]="employment.controls.inPaidEmployment"
            [lookUp]="accBooleanDisplayMap"
            [compactWarning]="true"
          />
        </div>

        @if (employment.isInPaidEmployment) {
          <div class="grid grid-cols-1 gap-4 p-4 md:grid-cols-2">
            <pr-acc-control-summary
              label="Is this claim for a work related injury?"
              [control]="employment.controls.workInjury"
              [lookUp]="accBooleanDisplayMap"
              [compactWarning]="true"
            />
            <pr-acc-control-summary
              label="What level of activity is involved at work?"
              [control]="employment.controls.workTypeCode"
              [lookUp]="accWorkTypeMap"
            />
          </div>

          <div
            class="grid grid-cols-1 gap-4 rounded-lg bg-blue-50 p-4 md:grid-cols-2"
          >
            <pr-acc-control-summary
              label="Please select the nature of employment"
              [control]="employment.controls.employmentStatusCode"
              [lookUp]="accEmploymentStatusMap"
              [compactWarning]="true"
            />
            @if (employment.displayOtherEmployment) {
              <pr-acc-control-summary
                label="Other Employment"
                [control]="employment.controls.otherEmployment"
              />
            }
          </div>

          @if (employment.displayEmployer) {
            <!-- <Employer Name & Address> -->
            <div class="grid grid-cols-1 gap-4 p-4 md:grid-cols-2">
              <pr-acc-control-summary
                label="Employer Name"
                [control]="employment.employer.employerName"
              />
              <div class="flex flex-col">
                <div class="font-bold">Employer Address</div>
                <pr-acc-control-summary
                  label="Address Line 1"
                  [showLabel]="false"
                  [control]="employment.employer.address.controls.line1"
                />
                <pr-acc-control-summary
                  label="Address Line 2"
                  [showLabel]="false"
                  [control]="employment.employer.address.controls.line2"
                />
                <pr-acc-control-summary
                  label="Suburb"
                  [showLabel]="false"
                  [control]="employment.employer.address.controls.suburb"
                />
                <pr-acc-control-summary
                  label="City"
                  [showLabel]="false"
                  [control]="employment.employer.address.controls.city"
                />
                <pr-acc-control-summary
                  label="Postcode"
                  [showLabel]="false"
                  [control]="employment.employer.address.controls.postCode"
                />
                <pr-acc-control-summary
                  label="Country"
                  [showLabel]="false"
                  [control]="employment.employer.address.controls.country"
                />
              </div>
            </div>
          }
        }
      </div>
    }
    <!-- Work Capacity -->
    @if (workCapacityForm(); as workCapacity) {
      @if (employmentForm().isEmployedOrSelfEmployed) {
        <div>
          <h2 class="!font-medium">Fitness for Work</h2>
        </div>
        <div class="flex flex-col">
          <div
            class="grid grid-cols-1 gap-4 rounded-lg bg-blue-50 p-4 md:grid-cols-2"
          >
            <pr-acc-control-summary
              label="Is the patient fit to continue normal work?"
              [control]="workCapacity.controls.canResumeNormalWork"
              [compactWarning]="true"
            />
          </div>

          @if (workCapacity.displayIncapacity) {
            @for (
              control of workCapacity.incapacityControls;
              track idx;
              let idx = $index;
              let odd = $odd
            ) {
              <div
                class="gap-y grid grid-cols-1 gap-4 p-4 md:grid-cols-2"
                [ngClass]="{ 'rounded-lg bg-blue-50': odd }"
              >
                <pr-acc-control-summary
                  label="Incapacity Type"
                  [control]="control.controls.incapacityType"
                  [lookUp]="accIncapacityType"
                  [reverseLookUp]="true"
                  [formatFn]="splitCamelCase"
                />
                @if (control.displaySelectedAlternativeWork) {
                  <pr-acc-control-summary
                    label="Alternative Work Type"
                    [control]="
                      control.selectedAlternativeWorkGroup.controls
                        .alternativeWorkTypeCode
                    "
                    [lookUp]="accWorkTypeMap"
                  />
                  <pr-acc-control-summary
                    label="Physical Restrictions"
                    [control]="
                      control.selectedAlternativeWorkGroup.controls
                        .physicalRestrictions
                    "
                    [lookUp]="accPhysicalRestriction"
                    [formatFn]="splitStringArray"
                  />
                  <pr-acc-control-summary
                    label="Restricted Hours Per Day"
                    [control]="
                      control.selectedAlternativeWorkGroup.controls
                        .restrictedHoursPerDay
                    "
                  />
                  <pr-acc-control-summary
                    label="Restriction Comment"
                    [control]="
                      control.selectedAlternativeWorkGroup.controls
                        .restrictionComment
                    "
                  />
                }
                <pr-acc-control-summary
                  label="Incapacity Date Range"
                  [control]="control.controls.incapacityDateRange"
                  [formatFn]="dateRangeFormatFn"
                />
              </div>
            }
          }
          <div
            class="grid grid-cols-1 gap-4 rounded-lg bg-blue-50 p-4 md:grid-cols-2"
          >
            <pr-acc-control-summary
              label="Date for returning to normal work"
              [control]="workCapacity.controls.returnToNormalWorkDate"
              [formatFn]="dateFormatFn"
              [compactWarning]="true"
            />
          </div>
        </div>
      }
    }
  </div>

  <!-- Diagnosis Details Section -->
  <div class="py-8">
    <div class="flex justify-between">
      <h2 class="!m-0 !font-medium">Diagnosis Details</h2>
      <button mat-icon-button (click)="editDiagnosisDetails()">
        <mat-icon>edit</mat-icon>
      </button>
    </div>

    @if (dentalDiagnosisForm(); as diagnosis) {
      <div class="flex flex-col gap-8">
        <!-- Teeth Section -->
        <div>
          <h2 class="!font-normal">Teeth Information</h2>
          @if (diagnosis.toothInjuryControls.length) {
            @for (
              control of diagnosis.toothInjuryControls;
              track idx;
              let idx = $index;
              let even = $even
            ) {
              <div
                class="gap-y grid grid-cols-1 gap-4 p-4 md:grid-cols-2"
                [ngClass]="{ 'rounded-lg bg-blue-50': even }"
              >
                <pr-acc-control-summary
                  label="Tooth Number"
                  [control]="control.controls.toothNumber"
                />
                <pr-acc-control-summary
                  label="Injury Classification"
                  [control]="control.controls.injuryClassification"
                  [lookUp]="accToothInjuryClassification"
                />
                <pr-acc-control-summary
                  label="Pre-Accident Condition"
                  [control]="control.controls.preAccidentCondition"
                  [lookUp]="accToothPreAccidentCondition"
                />
              </div>
            }
            <div class="grid grid-cols-1 gap-4 p-4 md:grid-cols-2">
              <pr-acc-control-summary
                label="Additional Injury Comments"
                [control]="diagnosis.additionalInjuryCommentControl"
              />
            </div>
          }
        </div>
        <!-- Prosthesis Section -->
        <div>
          <h2 class="!font-normal">Prosthesis Information</h2>
          <div
            class="grid grid-cols-1 gap-4 rounded-lg bg-blue-50 p-4 md:grid-cols-2"
          >
            <pr-acc-control-summary
              label="Was a prosthesis worn at the time of injury?"
              [control]="diagnosis.prosthesis.controls.wornAtTimeOfInjury"
              [lookUp]="accBooleanDisplayMap"
              [compactWarning]="true"
            />

            @if (diagnosis.prosthesis.displayProsthesisType) {
              <pr-acc-control-summary
                label="Prosthesis Type"
                [control]="diagnosis.prosthesis.controls.prosthesisType"
                [formatFn]="splitStringArray"
              />
              <pr-acc-control-summary
                label="Teeth on Partial Denture"
                [control]="diagnosis.prosthesis.controls.teethOnPartialDenture"
                [formatFn]="splitToothArray"
              />
            }
          </div>
        </div>
        <!-- Soft Tissue Section -->
        <div>
          <h2 class="!font-normal">Soft Tissue Information</h2>
          <div class="flex flex-col gap-2">
            <div>
              <p class="font-bold">Gingiva</p>
              <div
                class="grid grid-cols-1 gap-4 rounded-lg bg-blue-50 p-4 md:grid-cols-2"
              >
                <pr-acc-control-summary
                  label="How"
                  [control]="diagnosis.softTissue.gingiva.how"
                  [formatFn]="splitStringArray"
                />
                <pr-acc-control-summary
                  label="Position in Mouth"
                  [control]="diagnosis.softTissue.gingiva.positionInMouth"
                />
              </div>
            </div>

            <div>
              <p class="font-bold">Mucosa</p>
              <div
                class="grid grid-cols-1 gap-4 rounded-lg bg-blue-50 p-4 md:grid-cols-2"
              >
                <pr-acc-control-summary
                  label="How"
                  [control]="diagnosis.softTissue.mucosa.how"
                  [formatFn]="splitStringArray"
                />
                <pr-acc-control-summary
                  label="Position in Mouth"
                  [control]="diagnosis.softTissue.mucosa.positionInMouth"
                />
              </div>
            </div>

            <div>
              <p class="font-bold">Lip</p>
              <div
                class="grid grid-cols-1 gap-4 rounded-lg bg-blue-50 p-4 md:grid-cols-2"
              >
                <pr-acc-control-summary
                  label="How"
                  [control]="diagnosis.softTissue.lip.how"
                  [formatFn]="splitStringArray"
                />
                <pr-acc-control-summary
                  label="Position in Mouth"
                  [control]="diagnosis.softTissue.lip.positionInMouth"
                />
              </div>
            </div>

            <div class="grid grid-cols-1 gap-4 p-4 md:grid-cols-2">
              <pr-acc-control-summary
                label="Degloving Injury"
                [control]="diagnosis.softTissue.deglovingInjury"
                [formatFn]="splitStringArray"
              />
            </div>
          </div>
        </div>

        <!-- Jaw/Alveolus/TMJ Section -->
        <div>
          <h2 class="!font-normal">Jaw, Alveolus, TMJ Information</h2>
          <div
            class="grid grid-cols-1 gap-4 rounded-lg bg-blue-50 p-4 md:grid-cols-2"
          >
            <pr-acc-control-summary
              label="Alveolus Injuries"
              [control]="diagnosis.jawAlveolusTmj.controls.alveolusInjuries"
              [formatFn]="splitStringArray"
            />
            <pr-acc-control-summary
              label="Teeth Involved"
              [control]="diagnosis.jawAlveolusTmj.controls.teethInvolved"
              [formatFn]="splitToothArray"
            />
          </div>
          <div class="grid grid-cols-1 gap-4 p-4 md:grid-cols-2">
            <pr-acc-control-summary
              label="Jaw Injuries"
              [control]="diagnosis.jawAlveolusTmj.controls.jawInjuries"
              [formatFn]="splitStringArray"
            />
            <pr-acc-control-summary
              label="Type/Position"
              [control]="diagnosis.jawAlveolusTmj.controls.typePosition"
            />
          </div>
          <div
            class="grid grid-cols-1 gap-4 rounded-lg bg-blue-50 p-4 md:grid-cols-2"
          >
            <pr-acc-control-summary
              label="TMJ Injuries"
              [control]="diagnosis.jawAlveolusTmj.controls.tmjInjuries"
              [formatFn]="splitStringArray"
            />
            <pr-acc-control-summary
              label="Specific Injury"
              [control]="diagnosis.jawAlveolusTmj.controls.specificInjury"
            />
          </div>
        </div>
        <!-- Other Section -->
        <div>
          <h2 class="!font-normal">Other Dental Injury Information</h2>
          <div
            class="grid grid-cols-1 gap-4 rounded-lg bg-blue-50 p-4 md:grid-cols-2"
          >
            <pr-acc-control-summary
              label="Permanent teeth missing prior to accident?"
              [control]="
                diagnosis.other.controls.permanentTeethMissingPriorToAccident
              "
              [lookUp]="accBooleanDisplayMap"
              [compactWarning]="true"
            />
            <pr-acc-control-summary
              label="Teeth Missing"
              [control]="diagnosis.other.controls.teethMissing"
              [formatFn]="splitToothArray"
            />
          </div>
          <div class="grid grid-cols-1 gap-4 p-4 md:grid-cols-2">
            <pr-acc-control-summary
              label="Oral Hygiene"
              [control]="diagnosis.other.controls.oralHygiene"
              [lookUp]="accCondition"
            />
            <pr-acc-control-summary
              label="Periodontal Condition"
              [control]="diagnosis.other.controls.periodontalCondition"
              [lookUp]="accCondition"
            />
          </div>
          <div
            class="grid grid-cols-1 gap-4 rounded-lg bg-blue-50 p-4 md:grid-cols-2"
          >
            <pr-acc-control-summary
              label="Caries Activity in Mouth"
              [control]="diagnosis.other.controls.cariesActivityInMouth"
              [lookUp]="accCariesActivity"
            />
          </div>
          <div class="grid grid-cols-1 gap-4 p-4 md:grid-cols-2">
            <pr-acc-control-summary
              label="Were radiographs taken?"
              [control]="diagnosis.other.controls.radiographsTaken"
              [lookUp]="accBooleanDisplayMap"
            />
            @if (diagnosis.other.displayRadiographsTaken) {
              <pr-acc-control-summary
                label="Radiographs Type"
                [control]="diagnosis.other.controls.radiographsType"
              />
            }
          </div>
          <div
            class="grid grid-cols-1 gap-4 rounded-lg bg-blue-50 p-4 md:grid-cols-2"
          >
            <pr-acc-control-summary
              label="Is there photographic record of injury?"
              [control]="diagnosis.other.controls.photographicRecordOfInjury"
              [lookUp]="accBooleanDisplayMap"
              [compactWarning]="true"
            />
          </div>
        </div>
      </div>
    }
  </div>

  <!-- Other Assistance Section -->
  <div class="py-8">
    <div class="flex justify-between">
      <h2 class="!m-0 !font-medium">Other Assistance</h2>
      <button mat-icon-button (click)="editOtherAssistance()">
        <mat-icon>edit</mat-icon>
      </button>
    </div>

    @if (assistanceForm(); as assistance) {
      <div class="flex flex-col">
        <div
          class="grid grid-cols-1 gap-4 rounded-lg bg-blue-50 p-4 md:grid-cols-2"
        >
          <pr-acc-control-summary
            label="Was the patient admitted to hospital?"
            [control]="assistance.controls.admittedToHospital"
            [lookUp]="accBooleanDisplayMap"
            [compactWarning]="true"
          />
          <pr-acc-control-summary
            label="Is further assistance required?"
            [control]="assistance.controls.assistanceRequired"
            [lookUp]="accBooleanDisplayMap"
            [compactWarning]="true"
          />
        </div>

        <div class="grid grid-cols-1 gap-4 p-4 md:grid-cols-2">
          <pr-acc-control-summary
            label="Should ACC contact you, the provider?"
            [control]="assistance.controls.accContactProvider"
            [lookUp]="accRehabilitationMap"
            [displayMap]="accRehabilitationDisplayMap"
            [compactWarning]="true"
          />
        </div>
      </div>
    }

    @if (referralForm(); as referrals) {
      @if (referrals.referralControls.length) {
        @for (
          control of referrals.referralControls;
          track idx;
          let idx = $index;
          let even = $even
        ) {
          <div
            class="gap-y grid grid-cols-1 gap-4 p-4 md:grid-cols-2"
            [ngClass]="{ 'rounded-lg bg-blue-50': even }"
          >
            <pr-acc-control-summary
              label="Provider Type"
              [control]="control.controls.providerTypeCode"
              [lookUp]="accProviderTypeMap"
            />
            <pr-acc-control-summary
              label="Referral Reason"
              [control]="control.controls.referralReason"
            />
          </div>
        }
      }
    }
  </div>
</div>
